/**
 * Per-Unit QR Codes section for /settings/qr-codes.
 *
 * Lists every unit_qr_codes row in the caller's scope. Two presentations,
 * toggleable: a dense table (list) and a thumbnail tile grid. The user's
 * choice persists in localStorage between sessions.
 *
 * QR thumbnails are rendered client-side from the payload (qrcode.react).
 * They're identification-quality, not pixel-perfect reproductions of any
 * uploaded image — for the actual stored image, follow the Manage link
 * to /records/<unit_code>/qr.
 *
 * Manage links use unit_code (NOT unit_id UUID); the records router is
 * keyed on unit_code.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, QrCode, LayoutGrid, List } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { listAllUnitQrCodes } from '@/lib/qrCodeApi';
import type { UnitQrCodeListRow, QrCodeSource } from '@/types/qr';

const SOURCE_LABEL: Record<QrCodeSource, string> = {
  generated_v1: 'Generated',
  uploaded: 'Uploaded',
  uploaded_arbitrary: 'Override',
};

const VIEW_MODE_KEY = 'ht.qrCodes.unitView';
type ViewMode = 'grid' | 'list';

function readViewMode(): ViewMode {
  if (typeof window === 'undefined') return 'grid';
  const v = window.localStorage.getItem(VIEW_MODE_KEY);
  return v === 'list' ? 'list' : 'grid';
}

function statusBadge(row: UnitQrCodeListRow) {
  if (row.is_active) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] px-1.5 py-0 border-[#8FB560]/40 text-[#8FB560]"
      >
        Active
      </Badge>
    );
  }
  if (row.archived_at) {
    return (
      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
        Archived
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-[10px] px-1.5 py-0 text-muted-foreground"
    >
      Retired
    </Badge>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

interface QrThumbnailProps {
  payload: string;
  dim?: boolean;
  size?: number;
}

function QrThumbnail({ payload, dim = false, size = 96 }: QrThumbnailProps) {
  return (
    <div
      className={`relative rounded-md border border-border bg-white p-2 shrink-0 transition-opacity ${
        dim ? 'opacity-60' : ''
      }`}
      style={{ width: size, height: size }}
    >
      <QRCodeSVG
        value={payload}
        level="M"
        marginSize={0}
        width="100%"
        height="100%"
        bgColor="#ffffff"
        fgColor="#111111"
      />
    </div>
  );
}

interface ViewToggleProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
}

function ViewToggle({ mode, onChange }: ViewToggleProps) {
  return (
    <div
      role="group"
      aria-label="View mode"
      className="inline-flex items-center rounded-md border border-border overflow-hidden"
    >
      <button
        type="button"
        aria-label="Grid view"
        aria-pressed={mode === 'grid'}
        onClick={() => onChange('grid')}
        className={`h-7 w-7 flex items-center justify-center transition-colors ${
          mode === 'grid'
            ? 'bg-muted text-foreground'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        aria-label="List view"
        aria-pressed={mode === 'list'}
        onClick={() => onChange('list')}
        className={`h-7 w-7 flex items-center justify-center transition-colors border-l border-border ${
          mode === 'list'
            ? 'bg-muted text-foreground'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <List className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

interface RowProps {
  row: UnitQrCodeListRow;
}

function GridTile({ row }: RowProps) {
  const slug = row.unit_code ?? row.unit_id;
  return (
    <Link
      to={`/records/${encodeURIComponent(slug)}/qr`}
      className="group flex gap-3 p-3 rounded-md border border-border bg-card hover:bg-muted/30 transition-colors"
    >
      <QrThumbnail payload={row.qr_payload} dim={!row.is_active} />
      <div className="min-w-0 flex-1 flex flex-col gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-sm font-medium text-foreground truncate">
            {row.unit_code ?? row.unit_id.slice(0, 8)}
          </span>
          {statusBadge(row)}
        </div>
        <span className="text-[11px] text-muted-foreground">
          {SOURCE_LABEL[row.source]}
          {row.activated_at ? (
            <>
              {' · '}
              <span className="tabular-nums">
                {new Date(row.activated_at).toLocaleDateString()}
              </span>
            </>
          ) : null}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground/80 truncate">
          {truncate(row.qr_payload, 48)}
        </span>
      </div>
    </Link>
  );
}

function ListRow({ row }: RowProps) {
  const slug = row.unit_code ?? row.unit_id;
  return (
    <Link
      to={`/records/${encodeURIComponent(slug)}/qr`}
      className="grid grid-cols-[120px_72px_72px_1fr_88px] items-center gap-3 px-3 py-2 border-t border-border hover:bg-muted/20 transition-colors text-sm"
    >
      <span className="font-mono text-xs text-foreground truncate">
        {row.unit_code ?? row.unit_id.slice(0, 8)}
      </span>
      <span>{statusBadge(row)}</span>
      <span className="text-[11px] text-muted-foreground">
        {SOURCE_LABEL[row.source]}
      </span>
      <span className="font-mono text-[11px] text-muted-foreground/80 truncate">
        {truncate(row.qr_payload, 80)}
      </span>
      <span className="text-[11px] text-muted-foreground tabular-nums text-right">
        {row.activated_at
          ? new Date(row.activated_at).toLocaleDateString()
          : '—'}
      </span>
    </Link>
  );
}

export function UnitQrCodeList() {
  const [rows, setRows] = useState<UnitQrCodeListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setViewState] = useState<ViewMode>(() => readViewMode());

  const setView = (next: ViewMode) => {
    setViewState(next);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_MODE_KEY, next);
    }
  };

  useEffect(() => {
    let cancelled = false;
    listAllUnitQrCodes()
      .then((res) => {
        if (cancelled) return;
        setRows(res.qr_codes);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load per-unit QR codes.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            Per-Unit QR Codes
          </h3>
          <p className="text-xs text-muted-foreground">
            Auto-generated check-in QRs across the fleet. Click a row to
            manage history at <code className="font-mono">/records/&lt;unit&gt;/qr</code>.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {rows.length > 0 && (
            <span className="text-xs text-muted-foreground tabular-nums">
              {rows.length} {rows.length === 1 ? 'code' : 'codes'}
            </span>
          )}
          <ViewToggle mode={view} onChange={setView} />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-border rounded-lg">
          <QrCode className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            No per-unit QR codes in your scope yet
          </p>
        </div>
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {rows.map((row) => (
            <GridTile key={row.id} row={row} />
          ))}
        </div>
      ) : (
        <div className="border border-border rounded-md overflow-hidden">
          <div className="grid grid-cols-[120px_72px_72px_1fr_88px] gap-3 px-3 py-2 bg-muted/30 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
            <span>Unit</span>
            <span>Status</span>
            <span>Source</span>
            <span>Payload</span>
            <span className="text-right">Activated</span>
          </div>
          {rows.map((row) => (
            <ListRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}
